import Box from '@mui/material/Box'
import { getCountries } from 'libphonenumber-js'
import React from 'react'

import { SearchableSelect, Select } from '@/app/component/atom/form'
import { createConfirmation } from '@/app/contexts/confirmation.context'
import { LocalOpenEnded, OpenEndedFormat } from '@/app/module/campaigns/types'
import {
  normalizeOpenEndedValidation,
  validateOpenEndedFormat,
} from '@/app/module/campaigns/utils/open-ended-validation'
import { getCountryName } from '@/app/service/util/country'

import ResponseRegexInput from './response-regex-input'

type Props = {
  hasInvalidReplyActions: boolean
  openEnded: LocalOpenEnded
  onChange: (openEnded: LocalOpenEnded) => void
}

const validationOptions = [
  { label: 'Accept any reply', value: 'any' },
  { label: 'Local phone number', value: OpenEndedFormat.RegionPhoneNumber },
  { label: 'International phone number', value: OpenEndedFormat.IntPhoneNumber },
  { label: 'Email', value: OpenEndedFormat.Email },
  { label: 'Regular expression', value: OpenEndedFormat.Regexp },
]

const validationHelpText: Record<OpenEndedFormat, string> = {
  [OpenEndedFormat.RegionPhoneNumber]: 'Replies may be a local phone number or include the international prefix.',
  [OpenEndedFormat.IntPhoneNumber]: 'Replies must include a country calling code, with or without +.',
  [OpenEndedFormat.Email]: 'Replies must be a valid email address.',
  [OpenEndedFormat.Regexp]: 'Replies must match the specified regular expression.',
}

const countries = getCountries()
  .map((code) => ({ label: getCountryName(code) || code, value: code }))
  .sort((a, b) => a.label.localeCompare(b.label))

const ResponseOpenEndedValidation: React.FC<Props> = ({ hasInvalidReplyActions, openEnded, onChange }) => {
  const id = React.useId()
  const validationName = `reply-validation-${id}`
  const validationId = `select-${validationName}`
  const [errorsVisible, setErrorsVisible] = React.useState(true)
  const regexInputRef = React.useRef<HTMLInputElement>(null)
  const shouldFocusRegexRef = React.useRef(false)
  const normalized = normalizeOpenEndedValidation(openEnded)
  const { format = '', formatOption = '' } = normalized
  React.useEffect(() => {
    if (format === OpenEndedFormat.Regexp && shouldFocusRegexRef.current) {
      regexInputRef.current?.focus()
    }
    shouldFocusRegexRef.current = false
  }, [format])

  const countryError = errorsVisible ? validateOpenEndedFormat(normalized) : ''
  const handleFormatChange = async ({ value: selection }: { value: OpenEndedFormat | 'any' }) => {
    const value = selection === 'any' ? '' : selection
    if (!value && hasInvalidReplyActions) {
      const confirmed = await createConfirmation({
        title: 'Accept any reply',
        content: 'This will remove all "invalid reply" actions. Any reply will be considered valid.',
        confirmButtonLabel: 'Continue',
        cancelButtonLabel: 'Cancel',
      })
      if (!confirmed) {
        return
      }
    }

    const { format: _format, formatOption: _formatOption, ...rest } = normalized
    setErrorsVisible(false)
    shouldFocusRegexRef.current = value === OpenEndedFormat.Regexp
    onChange({
      ...rest,
      ...(value ? { format: value } : {}),
      ...(value === OpenEndedFormat.RegionPhoneNumber || value === OpenEndedFormat.Regexp ? { formatOption: '' } : {}),
    })
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '210px minmax(0, 1fr)' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Select<OpenEndedFormat | 'any'>
          InputProps={{
            id: undefined,
            inputProps: {
              labelId: `${validationId}-label`,
              SelectDisplayProps: { id: validationId, 'aria-labelledby': `${validationId}-label` },
            },
          }}
          id={validationId}
          info={format ? validationHelpText[format] : undefined}
          label="Reply validation"
          name={validationName}
          value={format || 'any'}
          values={validationOptions}
          onChange={handleFormatChange}
        />
        {format === OpenEndedFormat.RegionPhoneNumber && (
          <SearchableSelect
            error={!!countryError}
            helperText={countryError}
            id={`${id}-country`}
            keyPropFn={(option) => option.value}
            label="Country"
            options={countries}
            required
            showAll
            value={formatOption}
            valuePropFn={(option) => option.label}
            onBlur={() => setErrorsVisible(true)}
            onChange={(value) => onChange({ ...normalized, formatOption: value })}
          />
        )}
        {format === OpenEndedFormat.Regexp && (
          <ResponseRegexInput
            examplesPosition="below"
            inputRef={regexInputRef}
            required
            showErrorsOnMount={errorsVisible}
            value={formatOption}
            onChange={(value) => onChange({ ...normalized, formatOption: value })}
          />
        )}
      </Box>
    </Box>
  )
}

export default ResponseOpenEndedValidation
